import { toE164 } from "../../services/auth/PhoneVerification";
import {
  MAX_PROFILE_ENTRIES,
  genEntryId,
  normalizeEducation,
  normalizeWorkExperience,
  summarizeExperience,
  experiencePeriods,
  MAX_EXPERIENCE_PERIODS,
  EDUCATION_EVIDENCE_DOC,
  EXPERIENCE_EVIDENCE_DOC,
} from "../applicants/applicantLogic";

// Canonical applicant intake payload builder, shared by the registration wizard
// (ApplicantRegistrationForm) and the self-service profile (ApplicantContinue) so
// both write an identical shape to intakeApplicant. Takes the form object and
// produces the { education[], workExperience[] } lists the backend persists —
// both are repeatable: an applicant may register several degrees and several jobs.
//
// Note: immutable fields (firstName, lastName, idType) are still included
// here because the registration create needs them; on an existing applicant the
// backend ignores changes to those (see intakeApplicant immutability guard).

// Blank rows for the list editors (ProfileEntryLists). `inProgress` / `current`
// replace the end date (estudios en curso, empleo actual): neither list requires
// one. `foreignDegree` + `convalidated` record a degree obtained abroad and
// whether it was convalidated in Colombia.
// Each row is born with its `id`: it is what its certificate points at
// (file.entryId), so it must exist before the applicant attaches anything and
// must survive every round trip through the form.
export const emptyEducation = () => ({
  id: genEntryId(),
  level: "", degree: "", institution: "", graduationDate: "",
  inProgress: false, foreignDegree: false, convalidated: false,
});
// An experience entry is one company (one certificate) with one or more
// periods; position/startDate/endDate/current on the entry are only a summary
// of them, recomputed on save.
export const emptyPeriod = () => ({ position: "", startDate: "", endDate: "", current: false });
export const emptyExperience = () => ({
  id: genEntryId(),
  company: "", nit: "", description: "",
  periods: [emptyPeriod()],
});
const hasPeriod = (p) => !!(p && (p.position || p.startDate || p.endDate || p.current));

// An entry is "filled" once any of its fields carries something; blank rows are
// dropped on save instead of being stored.
export const hasEducationEntry = (e) =>
  !!(e && (e.level || e.degree || e.institution || e.graduationDate));
export const hasExperienceEntry = (e) =>
  !!(e && (e.company || e.nit || e.description
    || e.position || e.startDate || e.endDate
    || (Array.isArray(e.periods) && e.periods.some(hasPeriod))));

// Profile document keys backing each list. They are no longer per-postulación
// document types: each file certifies ONE entry and lives on the applicant.
export { EDUCATION_EVIDENCE_DOC, EXPERIENCE_EVIDENCE_DOC, MAX_PROFILE_ENTRIES };

const iso = (d) => (d ? new Date(d).toISOString() : null);
const txt = (v) => String(v ?? "").trim() || null;
// Stored dates are ISO; the date inputs expect `YYYY-MM-DD`.
const day = (d) => (d ? String(d).slice(0, 10) : "");

export function buildApplicantData(form) {
  return {
    nuip: String(form.nuip || "").trim(),
    firstName: form.firstName,
    lastName: form.lastName,
    email: form.email,
    mobilePhone: toE164(form.mobilePhone) || null,
    address: form.address || null,
    birthDate: iso(form.birthDate),
    idType: form.documentType,
    education: (form.education || [])
      .filter(hasEducationEntry)
      .slice(0, MAX_PROFILE_ENTRIES)
      .map((e) => ({
        id: e.id || genEntryId(),
        level: txt(e.level),
        degree: txt(e.degree),
        // Estudios en curso: no graduation date to send (the normalizers on both
        // sides clear it anyway; doing it here keeps the payload honest).
        graduationDate: e.inProgress ? null : iso(e.graduationDate),
        institution: txt(e.institution),
        inProgress: !!e.inProgress,
        foreignDegree: !!e.foreignDegree,
        convalidated: !!e.foreignDegree && !!e.convalidated,
      })),
    workExperience: (form.workExperience || [])
      .filter(hasExperienceEntry)
      .slice(0, MAX_PROFILE_ENTRIES)
      .map((e) => {
        const periods = experiencePeriods(e).filter(hasPeriod)
          .slice(0, MAX_EXPERIENCE_PERIODS)
          .map((p) => ({
            position: txt(p.position),
            startDate: iso(p.startDate),
            // Empleo actual: no end date.
            endDate: p.current ? null : iso(p.endDate),
            current: !!p.current,
          }));
        // The summary fields are what readers that predate periods use; the
        // backend recomputes them the same way.
        const summary = summarizeExperience({ periods });
        return {
          id: e.id || genEntryId(),
          company: txt(e.company),
          nit: txt(e.nit),
          description: txt(e.description),
          position: summary.position || null,
          startDate: summary.startDate || null,
          endDate: summary.endDate || null,
          current: !!summary.current,
          periods,
        };
      }),
  };
}

// Flattens a stored applicant document (as returned by getApplicant) back into
// the form shape buildApplicantData consumes — the inverse mapping, used to
// prefill the editable profile. Each list always comes back with at least one
// row so the editors have something to type into.
export function applicantToForm(applicant) {
  const a = applicant || {};
  const education = normalizeEducation(a.education).map((e) => ({
    ...e,
    graduationDate: day(e.graduationDate),
  }));
  // A legacy entry (no periods) comes back as its single period.
  const workExperience = normalizeWorkExperience(a.workExperience).map((e) => {
    const periods = experiencePeriods(e).map((p) => ({
      ...p,
      startDate: day(p.startDate),
      endDate: day(p.endDate),
    }));
    return { ...e, periods: periods.length ? periods : [emptyPeriod()] };
  });
  return {
    firstName: a.firstName || "",
    lastName: a.lastName || "",
    documentType: a.idType || "",
    nuip: a.nuip || "",
    email: a.email || "",
    mobilePhone: a.mobilePhone || "",
    address: a.address || "",
    birthDate: day(a.birthDate),
    education: education.length ? education : [emptyEducation()],
    workExperience: workExperience.length ? workExperience : [emptyExperience()],
  };
}
